import { useEffect } from "react";

interface MetaTag {
  name?: string;
  property?: string;
  content: string;
}

interface HelmetProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  meta?: MetaTag[];
  scriptLd?: object;
}

export default function Helmet({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = "website",
  twitterCard = "summary_large_image",
  meta = [],
  scriptLd,
}: HelmetProps) {
  useEffect(() => {
    if (title) {
      document.title = title;
    }

    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    if (description) setMetaTag("description", description);
    if (keywords) setMetaTag("keywords", keywords);

    if (ogTitle) setMetaTag("og:title", ogTitle, true);
    if (ogDescription) setMetaTag("og:description", ogDescription, true);
    if (ogImage) setMetaTag("og:image", ogImage, true);
    if (ogType) setMetaTag("og:type", ogType, true);

    if (twitterCard) setMetaTag("twitter:card", twitterCard);

    meta.forEach((m) => {
      if (m.name) setMetaTag(m.name, m.content);
      if (m.property) setMetaTag(m.property, m.content, true);
    });

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    if (scriptLd) {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(scriptLd, null, 2);
      document.head.appendChild(script);
    }

    return () => {
      if (scriptLd) {
        const s = document.querySelector('script[type="application/ld+json"]');
        if (s) s.remove();
      }
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogType, twitterCard, meta, scriptLd]);

  return null;
}